/**
 * Role access helper — thin wrapper around @passport/auth-session's fetchAuthToken().
 *
 * There is no shared "IsInRole"-style helper published anywhere in the monorepo yet
 * (checked: atlas-forms, @passport/*). The closest existing conventions are
 * atlas-forms's own `state-react` package's `useAuthStore().hasRole(role: string)`
 * (checks a plain `string[]` — same shape `fetchAuthToken().user.roles` already has)
 * and @passport/store's `authzStore.hasRole(roleId)` (a different, role-object shape,
 * not applicable here). `isInRole` below mirrors the `state-react` signature/behavior
 * against `fetchAuthToken()` directly, since GridControl-level UI gating doesn't have
 * (and shouldn't require) a mounted `state-react` store.
 */

import { fetchAuthToken } from '@passport/auth-session';

/**
 * True if the current session's user has at least one of `requiredRoles`.
 * - Undefined/empty `requiredRoles` → always true (nothing to gate).
 * - No live session, or `fetchAuthToken()` throws → false (fail closed for gated actions).
 */
export function isInRole(requiredRoles?: string[]): boolean {
  if (!requiredRoles || requiredRoles.length === 0) return true;
  try {
    const roles = fetchAuthToken()?.user?.roles ?? [];
    return requiredRoles.some((r) => roles.includes(r));
  } catch (err) {
    console.warn('[isInRole] fetchAuthToken() failed; denying gated action:', err);
    return false;
  }
}
